// src/views/pages/contact-us/supportAccordion.jsx
import React, { useState } from "react";
import {
  Accordion, AccordionBody, AccordionHeader, AccordionItem,
} from "reactstrap";

// ── FAQ content: document forgery detection ────────────────────────────────────
const FAQ_ITEMS = [
  // ── Getting started
  {
    id:  "1",
    q:   "What types of documents can InsightAI analyse for forgery?",
    a:   "InsightAI currently supports invoices and bills in PDF, PNG, JPG, WEBP, and TIFF formats up to 20 MB per file. The system extracts structured fields (vendor, amounts, GST, dates, line items) and cross-checks them for tampering, mathematical errors, and visual anomalies.",
  },
  {
    id:  "2",
    q:   "How do I upload a document for analysis?",
    a:   "From the Dashboard, click Documents → upload your file by dragging and dropping it into the upload zone or clicking to browse. The analysis starts automatically and results are streamed in real time via the progress indicator. Most documents complete in under 3 seconds.",
  },
  {
    id:  "3",
    q:   "What is the risk score and how is it calculated?",
    a:   "The risk score (0–100) is a weighted composite of all detected issues. Scores ≥ 90 are CRITICAL (near-certain fraud), 70–89 are HIGH, 40–69 are MEDIUM, and below 40 are LOW. Each check — numeric reconciliation, GST verification, font analysis, LLM visual inspection — contributes independently. A single very strong signal (like an AI-generated producer string) can push the score to HIGH even if other checks pass.",
  },

  // ── Detection checks
  {
    id:  "4",
    q:   "What does 'Feature Extraction' check?",
    a:   "GPT-4.1 Vision reads the document image and extracts structured fields: vendor name, GST number, invoice number, all line items (quantity × unit price), subtotal, tax breakdown, total, and amount in words. These fields feed every downstream check. If extraction fails, most other checks cannot run.",
  },
  {
    id:  "5",
    q:   "What is the Numeric Reconciliation check?",
    a:   "The rules engine verifies: (1) each line item's amount = quantity × unit price, (2) subtotal = sum of all line item amounts, (3) each tax is calculated correctly at the stated rate, (4) grand total = subtotal − discount + taxes, (5) amount in words matches the numeric total. Differences below ₹0.02 are treated as floating-point rounding and suppressed. Differences between ₹0.02–₹0.10 are flagged as very low priority. Differences above ₹5 or 0.5% of invoice value are flagged HIGH.",
  },
  {
    id:  "6",
    q:   "How does multi-rate GST reconciliation work?",
    a:   "When line items have different GST rates (e.g. Item A at 5%, Item B at 18%), InsightAI checks each item's CGST, SGST, and IGST amounts independently at that item's own rate — not a blended rate across all items. This catches a common Gemini/AI editing pattern where amounts are altered but the per-item taxes are not recalculated correctly.",
  },
  {
    id:  "7",
    q:   "What does the GST Verification check do?",
    a:   "InsightAI calls the live GST registry API to verify the GSTIN and fuzzy-match the vendor name. If the name on the invoice matches less than 80% with the registered legal/trade name, a VENDOR_MISMATCH issue is raised. If the GSTIN is not found, or if the registration status is 'Cancelled' or 'Inactive', a high-risk issue is raised regardless of name match.",
  },
  {
    id:  "8",
    q:   "What does 'LLM Visual Forgery' detect?",
    a:   "GPT-4.1 Vision examines the document image for: font weight inconsistencies (one field printed bolder than its peers), handwritten digit alterations (e.g. '1' extended to '7'), paste-over artefacts (rectangular uniform patches), JPEG compression blocks around suspect regions, and layout deviations. This check also looks for AI-generation signals: pixel-perfect alignment, generic template fonts, all-round prices, and uniform decorative headers.",
  },
  {
    id:  "9",
    q:   "How does AI-generated document detection work?",
    a:   "InsightAI checks the PDF metadata (/Creator, /Producer) against a list of 30+ known AI and invoice-generator tools (Google/Gemini, ChatGPT, Canva, Puppeteer, ReportLab, wkhtmltopdf, etc.). For image files, EXIF tags (Software, ProcessingSoftware) are read. When a match is found, a SOFTWARE_MISMATCH issue is raised at risk score 82. The LLM visual check simultaneously looks for visual fingerprints of AI generation.",
  },
  {
    id:  "10",
    q:   "What is the Font Manipulation check?",
    a:   "InsightAI measures ink density (fraction of dark pixels) for each field value in the document image. Values significantly bolder than their peers (z-score ≥ 2.2 among all field values, or ≥ 1.45× denser than their own label) are flagged as likely overprinted or replaced. This catches cases where a number was edited in an image editor — the replacement text has slightly different print characteristics.",
  },
  {
    id:  "11",
    q:   "What is the Paste-over Detection check?",
    a:   "OCR words with confidence below 55% are clustered spatially. If 3+ clusters of 4+ low-confidence words each appear on the same page, it suggests systematic erasure and replacement — a common physical tampering pattern. A single isolated low-confidence region is not flagged (that is normal for seals, watermarks, and handwriting).",
  },
  {
    id:  "12",
    q:   "What does the QR/Barcode Scan check?",
    a:   "All QR codes and barcodes in the document are decoded. UPI payment links are extracted and their payee name / VPA is cross-checked against the vendor name. Suspicious UPI addresses (e.g. personal Gmail/phone VPAs on a business invoice) are flagged. The raw decoded content is shown in the QR/Barcode tab of the analysis screen.",
  },

  // ── Common false positives
  {
    id:  "13",
    q:   "Why is my legitimate invoice being flagged as HIGH risk?",
    a:   "Common reasons: (1) The vendor's GST name differs from what they print on invoices — submit via 'GST Verification Issue' with the document ID and we'll review the match threshold. (2) The invoice was created with a generic PDF tool (ReportLab, wkhtmltopdf, Canva) that happens to match the AI-generator list — genuine invoices from these tools can be whitelisted per org. (3) A scanned copy has low OCR confidence in table cells — tighten your scanner settings or use a higher DPI scan.",
  },
  {
    id:  "14",
    q:   "Why does the amount-in-words check fail on my invoice?",
    a:   "The words-to-numbers parser handles standard Indian English including lakhs, crores, paisa, and hyphenated compounds (twenty-five). Common issues: (a) The LLM extracted a 3-decimal amount (e.g. ₹36,934.543) — differences below ₹0.02 are now suppressed. (b) The words use an unusual phrase like 'and fifty paise only' — submit the example and we will add it to the parser. (c) The total_amount_in_words field was not extracted correctly — check the 'Extracted Fields' tab.",
  },
  {
    id:  "15",
    q:   "The font manipulation check is flagging a genuine bold header as tampered. What can I do?",
    a:   "Bold headers are a known false-positive source. The detector compares VALUE fields (amounts, dates, IDs) against each other — not against headers. If a header is being treated as a field value, the OCR may have misaligned the label/value boundary. Try disabling 'Font Manipulation' in Detection Settings for that document type, or increase the z-score threshold by contacting support.",
  },

  // ── Detection settings
  {
    id:  "16",
    q:   "How do Detection Settings work?",
    a:   "Detection Settings let you turn individual checks on or off per document type. Changes are saved to your browser immediately (localStorage) and take effect on the next upload without a page reload. Click 'Save to Server' to persist them to your organisation account — colleagues logged into the same org will then use the same settings. The settings are snapshotted per document, so you can always see what was active when a document was analysed.",
  },
  {
    id:  "17",
    q:   "What are Custom Fields and how do I configure them?",
    a:   "Custom Fields let you define extra fields the LLM should extract from each document — for example, a PO Number or Project Code specific to your workflow. Click 'Custom Fields' on the upload screen, define your fields (key, display label, and type), and save. The 'Custom Fields Detection' toggle in Detection Settings controls whether these are included in analysis.",
  },

  // ── Duplicate & review workflow
  {
    id:  "18",
    q:   "How does duplicate invoice number detection work?",
    a:   "When a document is analysed, InsightAI checks whether the extracted invoice number or bill number already exists in any previously analysed document in your organisation. If duplicates are found, an amber pulsing '⚠ Duplicate Invoice' button appears in the top-right of the analysis screen. Clicking it shows a list of all matching documents you can navigate to directly.",
  },
  {
    id:  "19",
    q:   "How do I mark a document for review and filter by it?",
    a:   "In the analysis screen, click 'Mark for Review' in the top-right. The button turns green showing '🔖 Under Review'. On the document history screen, click the 'Under Review' filter tab to see only flagged documents. The count badge shows how many are currently under review. Documents marked for review also get a green left border on their card in the history grid.",
  },

  // ── Bulk / batch
  {
    id:  "20",
    q:   "How do I analyse multiple invoices at once?",
    a:   "Use the Bulk Analysis screen (link in the sidebar). You can upload up to 50 files at once via drag-and-drop, or provide an S3 directory URL with your AWS credentials. Documents are processed in parallel (1–10 workers, configurable). Results are available as you go and exportable as a full CSV report with all extracted fields, issue summaries, and risk scores.",
  },
  {
    id:  "21",
    q:   "What is included in the Batch CSV export?",
    a:   "The CSV includes: file name, document ID, risk score, risk level, invoice number, vendor name, total amount, all detected issue types and explanations, QR code data, GST verification result, and which detection checks were active. One row per document.",
  },

  // ── Privacy & security
  {
    id:  "22",
    q:   "Are my uploaded documents stored permanently?",
    a:   "Documents are stored in encrypted S3 storage per your organisation. Analysis results (extracted fields, issues, risk scores) are stored in the database scoped strictly to your organisation — no other organisation can access your data. Contact us to request deletion of specific documents or your entire organisation's data.",
  },
  {
    id:  "23",
    q:   "Is the document content sent to OpenAI or Google?",
    a:   "When LLM-based checks are enabled (Feature Extraction, LLM Visual Forgery, Risk Summary), the document text and a compressed JPEG image are sent to OpenAI's API using your or InsightAI's API key under standard OpenAI data processing terms. You can disable all LLM checks in Detection Settings to run a purely rules-based analysis that does not send data externally.",
  },
];

const SupportAccordion = () => {
  const [openId, setOpenId] = useState("");

  const toggle = (id) => setOpenId(prev => (prev === id ? "" : id));

  return (
    <div className="support-faq">
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #f3f4f6" }}>
        <h5 style={{ margin: 0, fontWeight: 800, fontSize: 14, color: "#111827" }}>
          Frequently Asked Questions
        </h5>
        <p style={{ margin: "4px 0 0", fontSize: 12, color: "#9ca3af" }}>
          {FAQ_ITEMS.length} questions about InsightAI document forgery detection
        </p>
      </div>

      <Accordion open={openId} toggle={toggle}>
        {FAQ_ITEMS.map(item => (
          <AccordionItem key={item.id}>
            <AccordionHeader targetId={item.id}>
              {item.q}
            </AccordionHeader>
            <AccordionBody accordionId={item.id}>
              {item.a}
            </AccordionBody>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default SupportAccordion;